import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// DPK press releases: newest on top, drag handle to reorder (support #1090,
// Amanda, 2026-09-08). Order is the `position` column, written from DOM
// order on every save, so the public DPK (dpk-reader orders by position
// too) shows exactly what this list shows.
export default class extends Controller {
  static targets = ["list", "row", "status"]
  static values = { url: String }

  connect() {
    this._saveTimer = null
    this.sortable = new Sortable(this.listTarget, {
      animation: 150,
      handle: "[data-press-releases-handle]",
      draggable: "[data-press-releases-target='row']",
      ghostClass: "opacity-30",
      onEnd: (event) => {
        if (event.oldDraggableIndex !== event.newDraggableIndex) this.save()
      }
    })
  }

  disconnect() {
    if (this.sortable) this.sortable.destroy()
  }

  addRow() {
    const row = document.createElement("div")
    row.className = "grid grid-cols-[auto_1fr_1fr_auto] gap-3 items-start"
    row.dataset.pressReleasesTarget = "row"
    row.dataset.prId = ""
    row.innerHTML = `
      ${this._handleMarkup()}
      <input type="text" class="mc-input" placeholder="Title" data-field="title" data-action="input->press-releases#queueSave">
      <input type="url" class="mc-input" placeholder="https://..." data-field="url" data-action="input->press-releases#queueSave">
      <button type="button" data-action="click->press-releases#removeRow" class="mt-1.5 text-[var(--color-mc-text-secondary)] hover:text-[#E57373] transition-colors" title="Remove">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    `
    // Newest first: the new row goes on top and takes position 0 on save.
    this.listTarget.prepend(row)
    row.querySelector("[data-field='title']")?.focus()
  }

  removeRow(event) {
    const row = event.currentTarget.closest("[data-press-releases-target='row']")
    if (row) {
      row.remove()
      this.save()
    }
  }

  queueSave() {
    if (this._saveTimer) clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this.save(), 600)
  }

  async save() {
    // rowTargets come back in document order, so DOM order IS the position.
    const rows = this.rowTargets
    const attrs = {}

    rows.forEach((row, index) => {
      const title = row.querySelector("[data-field='title']")?.value?.trim() || ""
      const url = row.querySelector("[data-field='url']")?.value?.trim() || ""
      if (!title && !url) return

      const id = row.dataset.prId
      attrs[index] = {
        title: title,
        url: url,
        position: index
      }
      if (id) attrs[index].id = id
    })

    this._showStatus("Saving...")

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          dpk_profile: { dpk_press_releases_attributes: attrs }
        })
      })

      if (response.ok) {
        await response.json()
        this._showStatus("Saved!")
        setTimeout(() => this._showStatus(""), 2000)
        // Reload to sync IDs for newly created records
        if (Object.values(attrs).some(a => !a.id)) {
          this._reloadSection()
        }
      } else {
        this._showStatus("Error saving")
      }
    } catch {
      this._showStatus("Error saving")
    }
  }

  _handleMarkup() {
    return `
      <span class="mt-2 cursor-grab text-[var(--color-mc-text-secondary)] select-none" data-press-releases-handle title="Drag to reorder" aria-label="Drag to reorder">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="7" cy="5" r="1.5"/><circle cx="13" cy="5" r="1.5"/><circle cx="7" cy="10" r="1.5"/><circle cx="13" cy="10" r="1.5"/><circle cx="7" cy="15" r="1.5"/><circle cx="13" cy="15" r="1.5"/></svg>
      </span>
    `
  }

  _showStatus(text) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = text
    }
  }

  async _reloadSection() {
    // Fetch the edit page and extract the press releases section. The
    // Sortable instance is bound to the list container, so it keeps
    // working with the replaced children.
    try {
      const response = await fetch(window.location.href, { headers: { "Accept": "text/html" } })
      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const newList = doc.querySelector("[data-press-releases-target='list']")
      if (newList) {
        this.listTarget.innerHTML = newList.innerHTML
      }
    } catch {
      // Silent fail; IDs will sync on next page load
    }
  }
}
